import { Fragment, useMemo, useRef } from "react";
import { FieldColumnName } from "../../fields/FieldColumnName";
import { FieldFileType } from "../../fields/FieldFileType";
import { PanelProps } from "../ModalCreateColumn";

interface PanelCreateColumnFileProps extends PanelProps {
  singleSelection: boolean;
}

export const PanelCreateColumnFile = ({
  autoFocus = true,
  isDefinitionEditable = true,
  fields,
  onFieldChange,
  singleSelection,
}: PanelCreateColumnFileProps) => {
  const nameFieldRef = useRef<HTMLInputElement>(null);

  // Parse file type from column definition
  const fileTypes = useMemo(() => {
    return fields.definition.value?.kind === "file"
      ? fields.definition.value.file_types
      : [];
  }, [fields.definition.value]);

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by FieldFileType)
   * refocuses itself after onChange is called. I believe this is a bug in EUI
   * but I think this is the best option in the absence of a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== nameFieldRef.current) {
        nameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  return (
    <Fragment>
      <FieldFileType
        autoFocus={autoFocus}
        disabled={!isDefinitionEditable}
        singleSelection={singleSelection}
        value={fileTypes}
        onChange={(value) => {
          // Update state
          onFieldChange("definition", {
            value: {
              kind: "file",
              file_types: value.map((v) => v.id),
            },
            isValid: true,
          });

          // Auto-fill name field if not previously edited
          if (value.length === 1 && !fields.name.isDirty) {
            onFieldChange("name", {
              value: value[0].name,
              isValid: true,
              isDirty: false,
            });
          }

          // Shift focus to name field
          focusNameField();
        }}
      />

      <FieldColumnName
        ref={nameFieldRef}
        value={fields.name.value}
        onChange={(field) => {
          onFieldChange("name", {
            value: field.value,
            isValid: field.isValid,
            isDirty: true,
          });
        }}
      />
    </Fragment>
  );
};
